import { useCallback, useState } from 'react';

import { workingDayService } from '@/api/domains/workingDay';
import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';


export interface UseWorkingDayEnableDisableResult {
  enableWorkingDay: (id: number) => Promise<boolean>;
  disableWorkingDay: (id: number) => Promise<boolean>;
  togglingIds: Set<number>;
}

export function useWorkingDayEnableDisable(): UseWorkingDayEnableDisableResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());

  const enableWorkingDay = useCallback(async (id: number): Promise<boolean> => {
    setTogglingIds((previous) => new Set(previous).add(id));

    try {
      const response = await workingDayService.toggleStatus(id, true);

      if (response.success) {
        showNotification('success', translate('schedule:workingDay.messages.enableSuccess'), 5000);
        return true;
      }

      showApiError(response, translate('schedule:workingDay.messages.enableError'));
      return false;
    } finally {
      setTogglingIds((previous) => {
        const next = new Set(previous);
        next.delete(id);
        return next;
      });
    }
  }, [showNotification, showApiError, translate]);

  const disableWorkingDay = useCallback(async (id: number): Promise<boolean> => {
    setTogglingIds((previous) => new Set(previous).add(id));

    try {
      const response = await workingDayService.toggleStatus(id, false);

      if (response.success) {
        showNotification('success', translate('schedule:workingDay.messages.disableSuccess'), 5000);
        return true;
      }

      showApiError(response, translate('schedule:workingDay.messages.disableError'));
      return false;
    } finally {
      setTogglingIds((previous) => {
        const next = new Set(previous);
        next.delete(id);
        return next;
      });
    }
  }, [showNotification, showApiError, translate]);

  return {
    enableWorkingDay,
    disableWorkingDay,
    togglingIds,
  };
}

export default useWorkingDayEnableDisable;
